import { makeArticle } from "./learnFactory";
import type { LearnFAQ } from "./learnShared";
import type { RelatedLink as RL } from "@/components/RelatedPages";

/**
 * /learn/cognitive-fx-vs-saint-tms
 *
 * PROMPT 6 — NAMED COMPARISON: "COGNITIVE FX vs SAINT®"
 * This is the ONE page on the site that names them; Prompts 1–5 stay unnamed.
 * Uses ONLY their own published words, quoted exactly and linked to the source.
 * No opinions, no words like "fake" or "scam", nothing about their concussion/brain-injury care.
 * Linked from /learn/saint-vs-accelerated-tms only (not the main menu).
 */

const faqs: LearnFAQ[] = [
  {
    question: "Does Cognitive FX offer SAINT®?",
    answer:
      "No. Cognitive FX offers its own fMRI-guided accelerated TMS protocol, which it describes as off-label and not tested in the SAINT clinical trials. It is not listed on Magnus Medical's SAINT® provider locator.",
  },
  {
    question: "How much does Cognitive FX TMS cost compared to SAINT®?",
    answer:
      "Cognitive FX lists $9,900 for its 5-day program, with travel and lodging extra, and says insurance is not billed. SAINT® at Neuro Wellness starts at $25,000 for the full course, and Medicare has a payment rate for SAINT®.",
  },
  {
    question: "Is Cognitive FX's fMRI-guided TMS FDA-cleared?",
    answer:
      "TMS devices are FDA-cleared, but Cognitive FX states its accelerated 5-day schedule, and treating teens, are off-label. SAINT® is the only FDA-cleared system for personalized fMRI-targeted accelerated TMS.",
  },
  {
    question: "What is the Cognitive FX cost vs SAINT® cost?",
    answer:
      'Cognitive FX lists $9,900 for its 5-day program, with travel and lodging extra, and says "Insurance: Not billed." SAINT® at Neuro Wellness starts at $25,000 for the full 5-day course, and Medicare has a hospital outpatient payment rate for SAINT® ($19,703, CMS, July 2025). The two prices buy two different treatments — a lower price for a different treatment is not a discount on SAINT®.',
  },
  {
    question:
      "How does Cognitive FX fMRI-guided TMS compare to SAINT® fMRI targeting?",
    answer:
      'Cognitive FX states it uses its own fMRI-based method "rather than using Magnus Medical\'s proprietary targeting system." SAINT® uses FDA-cleared SAINT® targeting software that personalizes the target from your fMRI scan (510(k) K220177). Using an fMRI scan alone is not what makes a treatment SAINT®.',
  },
  {
    question: "Is Cognitive FX TMS FDA-cleared?",
    answer:
      'TMS devices are FDA-cleared, but Cognitive FX describes its 5-day program as "our own protocol, delivered off-label" and states that "treating teens" is off-label. SAINT® is the FDA-cleared system (510(k) K220177, September 2022) for personalized fMRI-targeted accelerated TMS in adults 22+ with major depressive disorder.',
  },
  {
    question: "Does Cognitive FX take insurance?",
    answer:
      'No. Cognitive FX states "Insurance: Not billed." SAINT® has a Medicare hospital outpatient payment rate ($19,703, CMS, July 2025), and commercial plans may approve it via prior authorization. We verify your benefits for free before you commit.',
  },
  {
    question: "Cognitive FX one-day TMS (ONE-D) vs SAINT®?",
    answer:
      'SAINT® is a 5-day, FDA-cleared protocol (510(k) K220177). Compressing the course into a single day is an off-label schedule, not SAINT®. Cognitive FX describes its accelerated 5-day schedule as "our own protocol, delivered off-label." SAINT® is cleared for adults 22+ as a 5-day course; any one-day (ONE-D) version is a different, off-label protocol, not a faster SAINT®.',
  },
];

export const pageCognitiveFxVsSaint = makeArticle({
  slug: "cognitive-fx-vs-saint-tms",
  title: "Cognitive FX vs. SAINT® TMS: Is It the Same Treatment? (2026)",
  description:
    "Cognitive FX's $9,900 fMRI-guided accelerated TMS is not SAINT®. In their own words: off-label, their own protocol, not tested in the SAINT trials. See the side-by-side.",
  canonical: "/learn/cognitive-fx-vs-saint-tms",
  h1: "Cognitive FX vs. SAINT® TMS: What's the Difference?",
  breadcrumbLabel: "Cognitive FX vs SAINT® TMS",
  shortAnswer:
    'No, Cognitive FX\'s 5-day program is not SAINT®. Cognitive FX, a clinic in Provo, Utah, describes its $9,900 program as "our own protocol, delivered off-label" and says "our protocol has not been tested in the SAINT clinical trials." SAINT® is the FDA-cleared Magnus system, and Neuro Wellness TMS Centers of America is Florida\'s verified SAINT® provider. If you are in crisis, call or text 988.',
  keyTakeaways: [
    'Cognitive FX describes its 5-day TMS as "our own protocol, delivered off-label."',
    'Cognitive FX states: "our protocol has not been tested in the SAINT clinical trials."',
    "Cognitive FX uses its own fMRI targeting rather than Magnus Medical's FDA-cleared targeting system.",
    "SAINT® is FDA-cleared (510(k) K220177) for adults 22+ with major depressive disorder.",
    "Medicare has established a hospital outpatient payment rate for SAINT® ($19,703, CMS July 2025); Cognitive FX notes insurance is not billed.",
    "Neuro Wellness in Coral Springs, Florida is a verified SAINT® provider listed by Magnus Medical.",
  ],
  sections: [
    {
      heading: "Quick answer",
      paragraphs: [
        'No, Cognitive FX\'s 5-day program is not SAINT®. Cognitive FX, a clinic in Provo, Utah, describes its $9,900 program as "our own protocol, delivered off-label" and says "our protocol has not been tested in the SAINT clinical trials." SAINT® is the FDA-cleared Magnus system, and Neuro Wellness TMS Centers of America is Florida\'s verified SAINT® provider.',
      ],
    },
    {
      heading: "Side by side",
      paragraphs: [],
      table: {
        headers: [
          "",
          "SAINT® at Neuro Wellness",
          "Cognitive FX accelerated fMRI-TMS (as published by Cognitive FX)",
        ],
        rows: [
          [
            "What it is",
            "SAINT®, FDA-cleared (510(k) K220177, Sept 2022)",
            '"our own protocol, delivered off-label" [1]',
          ],
          [
            "Targeting",
            "FDA-cleared SAINT® targeting software",
            '"rather than using Magnus Medical\'s proprietary targeting system, Cognitive FX uses its own fMRI-based method" [1]',
          ],
          [
            "Trial evidence",
            "78.6% remission in the 2022 randomized controlled trial (Cole et al., Am J Psychiatry 2022;179:132–141)",
            '"our protocol has not been tested in the SAINT clinical trials" [1]',
          ],
          [
            "Ages",
            "Adults 22+",
            '"adults and teens (14 and up)"; "treating teens, are off-label" [2]',
          ],
          [
            "Insurance",
            "Medicare payment rate ($19,703 hospital outpatient, CMS, July 2025); commercial plans may approve via prior authorization",
            '"Insurance: Not billed" [2]',
          ],
          [
            "Price",
            "From $25,000 for the full 5-day SAINT® course",
            '"$9,900 for the week, including the fMRI scan, targeting, TMS, CBT, physical therapy, massage, photobiomodulation and follow-up. Travel and lodging are extra." [2]',
          ],
          [
            "Verified on Magnus Medical's provider locator",
            "Yes (Florida)",
            "No",
          ],
        ],
      },
    },
    {
      heading: "Why the prices can't be compared",
      paragraphs: [
        "$9,900 and $25,000 are prices for two different treatments. Cognitive FX's price buys its own off-label protocol plus extras like massage and physical therapy. SAINT's price buys the FDA-cleared system and the protocol that produced the trial results. A lower price for a different treatment is not a discount on SAINT®.",
      ],
    },
    {
      heading: "Does SAINT's 79% remission rate apply to Cognitive FX?",
      paragraphs: [
        "No. Those results come from SAINT® trial patients. Cognitive FX says its protocol was not tested in those trials.",
      ],
    },
    {
      heading: "Cognitive FX cost vs SAINT® cost",
      paragraphs: [
        "Cognitive FX lists $9,900 for its 5-day program, with travel and lodging extra, and states \"Insurance: Not billed\" [2]. SAINT® at Neuro Wellness starts at $25,000 for the full 5-day course, and Medicare has a hospital outpatient payment rate for SAINT® of $19,703 (CMS, July 2025). The two prices buy two different treatments: Cognitive FX's price buys its own off-label protocol plus extras like massage and physical therapy, while SAINT®'s price buys the FDA-cleared system and the protocol that produced the trial results. A lower price for a different treatment is not a discount on SAINT®.",
      ],
    },
    {
      heading: "Cognitive FX fMRI-guided TMS vs SAINT® fMRI targeting",
      paragraphs: [
        'Cognitive FX states it uses its own fMRI-based method "rather than using Magnus Medical\'s proprietary targeting system" [1]. SAINT® uses FDA-cleared SAINT® targeting software that personalizes the target from your fMRI scan (510(k) K220177). Using an fMRI scan alone is not what makes a treatment SAINT® — the FDA-cleared targeting software and the protocol tested in the SAINT trials are.',
      ],
    },
    {
      heading: "Is Cognitive FX TMS FDA-cleared?",
      paragraphs: [
        'TMS devices are FDA-cleared, but Cognitive FX describes its 5-day program as "our own protocol, delivered off-label" [1] and states that "treating teens" is off-label [2]. SAINT® is the FDA-cleared system (510(k) K220177, September 2022) for personalized fMRI-targeted accelerated TMS, cleared for adults 22+ with major depressive disorder. An off-label schedule is not the same as an FDA-cleared treatment.',
      ],
    },
    {
      heading: "Does Cognitive FX take insurance?",
      paragraphs: [
        'No. Cognitive FX states "Insurance: Not billed" [2]. SAINT® has a Medicare hospital outpatient payment rate ($19,703, CMS, July 2025), and commercial plans may approve it via prior authorization. We verify your insurance benefits for free before you commit.',
      ],
    },
    {
      heading: "Cognitive FX one-day TMS (ONE-D) vs SAINT®",
      paragraphs: [
        'SAINT® is a 5-day, FDA-cleared protocol (510(k) K220177). Compressing the course into a single day is an off-label schedule, not SAINT®. Cognitive FX describes its accelerated 5-day schedule as "our own protocol, delivered off-label" [1]; any one-day (ONE-D) version is a different, off-label protocol, not a faster SAINT®. SAINT® is cleared for adults 22+ as a 5-day course.',
      ],
    },
  ],
  faqs,
  sources: [
    {
      citation:
        "[1] Cognitive FX. SAINT Treatment Cost: How Does It Compare to Other Accelerated TMS? Published by Cognitive FX, Provo, UT. Accessed November 3, 2026.",
      url: "https://www.cognitivefxusa.com/blog/saint-treatment-cost",
    },
    {
      citation:
        "[2] Cognitive FX. Precision Mental Health: fMRI-Guided Transcranial Magnetic Stimulation. Published by Cognitive FX, Provo, UT. Accessed November 3, 2026.",
      url: "https://www.cognitivefxusa.com/precision-mental-health-fmri-guided-transcranial-magnetic-stim",
    },
    {
      citation:
        "Cole EJ, et al. Stanford Neuromodulation Therapy (SNT): A Double-Blind Randomized Controlled Trial. Am J Psychiatry. 2022;179(2):132-141.",
      url: "https://pubmed.ncbi.nlm.nih.gov/35624717/",
    },
    {
      citation:
        "U.S. Food and Drug Administration. 510(k) Premarket Notification — Magnus Neuromodulation System with SAINT Technology (K220177, September 2022).",
      url: "https://www.accessdata.fda.gov/scripts/cdrh/cfdocs/cfpmn/pmn.cfm",
    },
    {
      citation:
        "Centers for Medicare & Medicaid Services. Hospital Outpatient Prospective Payment System — SAINT TMS payment rate (CMS, July 2025).",
    },
  ],
  relatedLinks: [
    {
      title: "SAINT\u00ae Disclaimer: Look-Alike Programs Are Not SAINT\u00ae",
      href: "/learn/saint-disclaimer",
      description:
        "Our formal disclaimer: Cognitive FX and other \u201cfMRI-guided\u201d programs are not SAINT\u00ae",
    },
    {
      title: 'SAINT\u00ae vs $9,900 "Accelerated TMS": Not the Same Treatment',
      href: "/learn/saint-vs-accelerated-tms",
      description:
        "Side-by-side comparison of FDA status, targeting, and trial evidence",
    },
    {
      title: "Is It Really SAINT\u00ae? How to Verify a Clinic",
      href: "/learn/what-is-a-verified-saint-provider",
      description: "A 3-step check and red flags before you pay",
    },
    {
      title: "Who Qualifies for SAINT\u00ae TMS?",
      href: "/learn/who-qualifies-for-saint-tms",
      description: "Eligibility, checklists, and next steps",
    },
    {
      title: "What Is SAINT\u00ae TMS?",
      href: "/learn/what-is-saint-tms",
      description: "The FDA-cleared, fMRI-guided 5-day protocol explained",
    },
    {
      title: "SAINT\u00ae TMS Cost",
      href: "/learn/saint-tms-cost",
      description:
        "What SAINT\u00ae costs, Medicare information, and payment options",
    },
  ] as RL[],
  reviewBlock: {
    writtenBy: "Neuro Wellness TMS Centers of America",
    reviewedBy: "Daniel Pietras, MD",
    reviewedByHref: "/dr-daniel-pietras-md",
    published: "November 3, 2026",
    lastReviewed: "November 3, 2026",
    lastUpdated: "November 3, 2026",
  },
  toc: [
    { label: "Quick answer", href: "#quick-answer" },
    { label: "Side by side", href: "#side-by-side" },
    {
      label: "Why prices can't be compared",
      href: "#why-the-prices-cant-be-compared",
    },
    {
      label: "Does 79% remission apply?",
      href: "#does-saints-79-remission-rate-apply-to-cognitive-fx",
    },
    {
      label: "Cognitive FX cost vs SAINT® cost",
      href: "#cognitive-fx-cost-vs-saint-cost",
    },
    {
      label: "Cognitive FX fMRI vs SAINT® targeting",
      href: "#cognitive-fx-fmri-guided-tms-vs-saint-fmri-targeting",
    },
    {
      label: "Is Cognitive FX TMS FDA-cleared?",
      href: "#is-cognitive-fx-tms-fda-cleared",
    },
    {
      label: "Does Cognitive FX take insurance?",
      href: "#does-cognitive-fx-take-insurance",
    },
    {
      label: "ONE-D vs SAINT®",
      href: "#cognitive-fx-one-day-tms-one-d-vs-saint",
    },
    { label: "FAQ", href: "#faq" },
    { label: "Sources", href: "#cited-research" },
  ],
  safetyBlock:
    "Individual results vary. No outcome is guaranteed. Coverage varies by plan. SAINT® is contraindicated for people with non-removable magnetic-sensitive metal in or near the head, certain implanted devices, or increased intracranial pressure. Possible side effects include headache, fatigue, scalp discomfort and anxiety; TMS carries a small risk of seizure and, as with any depression treatment, a risk of worsening depression or suicidal thoughts. If you are in crisis, call or text 988.",
  trademarkFooter:
    "Cognitive FX is a trademark of its owner. SAINT® is a registered trademark of Magnus Medical. Neuro Wellness TMS Centers of America is not affiliated with Cognitive FX. Individual results vary. No outcome is guaranteed. Coverage varies by plan. Quotes from Cognitive FX published materials were accessed November 3, 2026.",
  ctaBand: {
    headline: "See if SAINT® is a fit, free",
    primaryText: "See if I may be a candidate",
    primaryHref: "/learn/who-qualifies-for-saint-tms",
    secondaryText: "Talk to a real person",
    secondaryHref: "tel:9548279793",
    phone: "954-827-9793",
  },
});

// Add Article schema with datePublished + dateModified
const baseGraph =
  (pageCognitiveFxVsSaint.schemaJson as { "@graph": unknown[] })["@graph"] ||
  [];
pageCognitiveFxVsSaint.schemaJson = {
  "@context": "https://schema.org",
  "@graph": [
    ...baseGraph,
    {
      "@type": "Article",
      "@id":
        "https://neurowellnesstms.com/learn/cognitive-fx-vs-saint-tms#article",
      headline: "Cognitive FX vs. SAINT® TMS: What's the Difference?",
      description:
        "Cognitive FX's $9,900 fMRI-guided accelerated TMS is not SAINT®. In their own words: off-label, their own protocol, not tested in the SAINT trials. See the side-by-side.",
      datePublished: "2026-11-03",
      dateModified: "2026-11-03",
      mainEntityOfPage:
        "https://neurowellnesstms.com/learn/cognitive-fx-vs-saint-tms",
      author: {
        "@type": "MedicalOrganization",
        name: "Neuro Wellness TMS Centers of America",
      },
      publisher: {
        "@type": "MedicalOrganization",
        name: "Neuro Wellness TMS Centers of America",
        url: "https://neurowellnesstms.com/",
      },
    },
  ],
};
